import type { Team, TeamPlan, User } from "@alch/dx-entities";
import { SearchParam } from "@hooks/useSearchParam";
import {
  identifyCustomerioUser,
  initCustomerio,
  resetCustomerioUser,
} from "@util/analytics/customerio";
import { StorageKey } from "@util/storageKeys";
import { IS_PRODUCTION, IS_STAGING } from "../deployEnv.js";
import {
  captureGoogleAnalyticsConversion,
  initGoogleAnalytics,
} from "./googleAnalytics.js";
import type { HeapProperties } from "./heap.js";
import {
  addHeapOnboardingProperties,
  addHeapRecommendationProperties,
  captureHeapEvent,
  identifyHeapUser,
  initHeap,
} from "./heap.js";

export enum EventPrefix {
  SignupFlowRedesign = "SFR",
  SpendingCap = "SpendingCap",
  Sandbox = "Sandbox",
  Alerts = "Alerts",
  Onboarding = "Onboarding",
  Auth = "Auth",
  PredepositGasCredits = "PredepositGasCredits",
  GasManager = "GasManager",
  HomeRecBanner = "HomeRecBanner",
  UpgradeOnboarding = "UpgradeOnboarding",
  SearchBar = "SearchBar",
  Products = "Products",
  Navbar = "Navbar",
  /** @deprecated use `SmartWallets` instead */
  EmbeddedAccounts = "EmbeddedAccounts",
  SmartWallets = "SmartWallets",
  Webhooks = "Webhooks",
  AppDetails = "AppDetails",
  ProductLanding = "ProductLanding",
  CreateApp = "CreateApp",
  AppSetup = "AppSetup",
  Rollups = "Rollups",
  ScheduleCallPopover = "ScheduleCallPopover",
  Chains = "Chains",
  Logs = "Logs",
  WalletApiLogs = "WalletApiLogs",
}

export const DEBUG_ANALYTICS =
  typeof window !== "undefined" &&
  !!window.localStorage.getItem(StorageKey.DEBUG_ANALYTICS);
let initialized = false;

// By default analytics are disabled in local dev. To enable run:
//   localStorage.setItem("ENABLE_ANALYTICS", "true")
const isEnabled = () =>
  !!window.localStorage.getItem(StorageKey.FORCE_ANALYTICS) ||
  ((IS_PRODUCTION ||
    IS_STAGING ||
    !!window.localStorage.getItem(StorageKey.ENABLE_ANALYTICS)) &&
    !currentlyViewingAs(window.location.search));

/**
 * Call this once, preferably at the root scope (not in a react render method)
 * An exception is for gatsby apps with SSR, since we don't want to run it server side
 */
export function initAnalytics() {
  if (!isEnabled() || initialized) return;

  initHeap(DEBUG_ANALYTICS);
  initGoogleAnalytics(DEBUG_ANALYTICS);
  initCustomerio(DEBUG_ANALYTICS);

  initialized = true;
}

/**
 * Note: If called a second time with a different ID, this will switch to reporting events as
 * the new user.  It will not merge/alias the two users (which is good).
 *
 * Also, when using view_as_user, this should only be called with the original staff
 * user.  Do not identify as the user you are viewing the page as.
 */
export function setAnalyticsUser({
  user,
  team,
  activePlan,
}: {
  user: User;
  team?: Team;
  activePlan?: TeamPlan;
}): void {
  if (!isEnabled() || !initialized) return;

  identifyHeapUser(
    user.id,
    {
      firstName: user.firstName,
      lastName: user.lastName,
      createdAt: new Date(user.createdAt).getTime(), // Integer timestamp
      email: user.email,
      teamId: team?.id,
      teamName: team?.name ?? undefined,
      isBillingAdmin: user.isBillingAdmin,
      referredBy: team?.referredBy ?? undefined,
      referredByAffiliate: team?.referredByAffiliate ?? undefined,
      tier: activePlan?.tier,
    },
    DEBUG_ANALYTICS,
  );
  identifyCustomerioUser({ user, team, activePlan }, DEBUG_ANALYTICS);
}

export function logoutAnalyticsUser() {
  if (!isEnabled() || !initialized) return;

  resetCustomerioUser(DEBUG_ANALYTICS);
}

/**
 * Under the hood, calls heap.track(eventName, properties).
 * https://developers.heap.io/reference/track
 *
 * @param eventName name of the custom event (this defines how events are grouped), e.g. "ChatWeb3: Clicked Copy Button". Parametrize instances of the same event with properties, e.g. { "messageId": "123" }. Do not use eventName to distinguish instances of the same event -- this will break grouping. See https://alchemyinsights.slack.com/archives/C04258L1UJG/p1701113998680779?thread_ts=1701113604.711059&cid=C04258L1UJG for bad things that happen when you do this.
 * @param properties object used to enrich the event instance with additional parameters.
 */
export function analyticsEvent(eventName: string, properties?: HeapProperties) {
  if (!isEnabled() || !initialized) return;
  captureHeapEvent(eventName, properties, DEBUG_ANALYTICS);
}

export function analyticsConversionEvent() {
  if (!isEnabled() || !initialized) return;
  captureGoogleAnalyticsConversion(DEBUG_ANALYTICS);
}

export function addOnboardingProperties(props: {
  onboardingResources?: string[];
  onboardingRequest?: {
    defaultMethod: string;
    method: string;
    language: string;
  };
}) {
  if (!isEnabled() || !initialized) return;
  addHeapOnboardingProperties(props, DEBUG_ANALYTICS);
}

export function addRecommendationProperties(props: {
  homeRecommendedProduct: string;
}) {
  if (!isEnabled() || !initialized) return;
  addHeapRecommendationProperties(props, DEBUG_ANALYTICS);
}

export function currentlyViewingAs(search: string) {
  const params = new URLSearchParams(search);
  return !!(
    params.get(SearchParam.ViewAsUser) ||
    params.get(SearchParam.ViewAsTeam) ||
    // view_as is deprecated for view_as_user but still needs to be supported
    params.get("view_as")
  );
}
